import { getApi } from '../utils/api';
import { API_ENDPOINTS } from '../utils/endpoints';
import { logError } from '../utils';

export const orderService = {
  /**
   * Fetches allowed products for a customer
   * @param {number} customerId - ID of the customer
   * @param {string} market - Market code
   * @returns {Promise<Array>} List of allowed products
   */
  getProducts: async (customerId, market) => {
    try {
      const response = await getApi().get(API_ENDPOINTS.ORDERS.PRODUCTS(customerId, market));
      return response.data;
    } catch (error) {
      logError(error, 'orderService.getProducts', { customerId, market });
      return [];
    }
  },

  /**
   * Fetches the list of orders for the current user
   * @returns {Promise<Array>} List of orders
   */
  getOrders: async (customerId, market, page = 1, pageSize = 10) => {
    try {
      // Get base URL
      const baseUrl = API_ENDPOINTS.ORDERS.LIST(customerId, market);
      // Add pagination params with correct separator
      const separator = baseUrl.includes('?') ? '&' : '?';
      const url = `${baseUrl}${separator}page=${page}&page_size=${pageSize}`;
      const response = await getApi().get(url);
      return response.data;
    } catch (error) {
      logError(error, 'orderService.getOrders');
      return { items: [], total: 0, page: 1, page_size: pageSize, total_pages: 1 };
    }
  },

  /**
   * Fetches details for a specific order
   * @param {string} orderId - ID of the order to fetch
   * @returns {Promise<Object>} Order details
   */
  getOrderDetails: async (orderNumber, market, customerId) => {
    try {
      const response = await getApi().get(API_ENDPOINTS.ORDERS.DETAILS(orderNumber, market, customerId));
      return response.data;
    } catch (error) {
      logError(error, 'orderService.getOrderDetails', { orderNumber });
      return null;
    }
  },

  /**
   * Modifies an existing order
   * @param {string} orderId - ID of the order to modify
   * @param {Object} changes - Changes to apply to the order
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  modifyOrder: async (customerId, orderId, changes) => {
    try {
      return await getApi().patch(API_ENDPOINTS.ORDERS.MODIFY(customerId, orderId), changes);
    } catch (error) {
      logError(error, 'orderService.modifyOrder', { orderId, changes });
      return {
        success: false,
        message: 'Failed to modify order'
      };
    }
  },

  /**
   * Fetches standing orders for a customer
   * @param {number} customerId - ID of the customer
   * @param {string} market - Market code
   * @returns {Promise<Array>} List of standing orders grouped by date range
   */
  getStandingOrders: async (customerId, market) => {
    try {
      const response = await getApi().get(API_ENDPOINTS.ORDERS.STANDING(customerId, market));
      return response.data;
    } catch (error) {
      logError(error, 'orderService.getStandingOrders', { customerId, market });
      return [];
    }
  },

  /**
   * Updates standing orders for a customer
   * @param {number} customerId - ID of the customer
   * @param {Object} payload - Standing order data
   * @returns {Promise<Object>} API response
   */
  updateStandingOrders: async (customerId, payload) => {
    try {
      const url = `/api/v1/stores/${customerId}/standing-orders`;
      const response = await getApi().put(url, payload);
      return response.data;
    } catch (error) {
      logError(error, 'orderService.updateStandingOrders', { customerId, payload });
      throw error;
    }
  },

  async getOrderHistory(customerId, market, startDate, endDate) {
    const url = API_ENDPOINTS.ORDERS.HISTORY(customerId);
    console.log('[getOrderHistory] url:', url, 'params:', { market, start_date: startDate, end_date: endDate });
    const response = await getApi().get(
      url,
      {
        params: {
          market,
          start_date: startDate,
          end_date: endDate
        }
      }
    );
    return response.data;
  }
};
